import { useMutation } from "@apollo/client"
import { Classes, Icon } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomCard from "components/Atoms/CustomCard"
import CustomTag from "components/Atoms/CustomTag"
import StringInput from "components/Atoms/StringInput"
import CustomReactTable from "components/Templates/CustomReactTable"
import FileUploadDialog from "components/Templates/FileUploadDialog"
import dayjs from "dayjs"
import {
  useDmsTripDetailsQuery,
  useItemDetailsLazyQuery,
  useSaveItemDetailsMutation,
} from "generated/graphql"
import { RESOLVE_REMARK } from "graphql/dms/dms.mutations.gql"
import React, { useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { SuccessToast } from "utils/AppToaster"
import classNames from "utils/classNames"

const ItemDetail = () => {
  const navigate = useNavigate()
  const { id, itemId } = useParams()
  const { data } = useDmsTripDetailsQuery({
    variables: { tripIdInt: Number(id), tripId: id },
  })

  const [getItemDetails, { data: items }] = useItemDetailsLazyQuery({
    variables: { tripId: id, sellableItemIds: [itemId] },
    fetchPolicy: "network-only",
  })

  const [totalOrdered, setTotalOrdered] = useState(
    items?.siByBooking[0]?.requiredQuantity
  )
  const [totalLoaded, setTotalLoaded] = useState(
    items?.siByBooking[0]?.despatchedQuantity
  )

  const [refresh, setRefresh] = useState(new Date().getTime())
  const [state, setState] = useState([])
  const [saveItemDetails] = useSaveItemDetailsMutation()
  const [resolveRemark] = useMutation(RESOLVE_REMARK)
  const editableKeyToFocus = useRef(null)

  useEffect(() => {
    getItemDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh])

  useEffect(() => {
    const newItems = items?.siByBooking[0]
    setTotalOrdered(newItems?.requiredQuantity)
    setTotalLoaded(newItems?.despatchedQuantity)
    const newState = newItems?.orders?.map(order => ({
      bookingId: order?.orderItems[0]?.itemBookings[0]?.id,
      despatchedQuantity:
        order?.orderItems[0]?.itemBookings[0]?.despatchedQuantity !== null
          ? order?.orderItems[0]?.itemBookings[0]?.despatchedQuantity
          : order?.orderItems[0]?.quantity,
      remark: order?.orderItems[0]?.itemBookings[0]?.driverDespatchRemark,
      despatchPhotoLink: order?.orderItems[0]?.itemBookings[0]?.despatchPhoto,
      loadingSequence: order?.orderItems[0]?.itemBookings[0]?.loadingSequence,
      itemId: order?.orderItems[0]?.sellableItem?.id,
      itemName: order?.orderItems[0]?.sellableItem?.name,
      quantity: order?.orderItems[0]?.quantity,
      orderId: order?.trackingCode,
      customerName: order?.user?.fullName,
      address: order?.shippingAddress?.address,
      fileUploadOpen: false,
    }))
    setState(newState)
  }, [items])

  const renderEditableCell = ({ original, index, value: quantity }) => {
    return (
      <div key={index + "row"}>
        <div className="vt-text-center">
          <StringInput
            key={index + "input"}
            autoFocus={index + "input" === editableKeyToFocus.current}
            type="number"
            value={state[index]?.despatchedQuantity}
            onChange={(path, value) => {
              //if (value > quantity) return;
              editableKeyToFocus.current = index + "input"
              const tempState = [...state]
              tempState[index].despatchedQuantity = value
              setState(tempState)
            }}
          />
        </div>
        {state &&
          state?.length > 0 &&
          state[index].remark &&
          state[index].remark !== "" && (
            <CustomButton
              style={{ float: "right" }}
              className="px-2"
              intent="warning"
              text="Resolve"
              onClick={() => {
                resolveRemark({
                  variables: {
                    tripId: id,
                    tripIdID: id,
                    bookings: [
                      {
                        bookingId: state[index]?.bookingId,
                        driverVerification: true,
                        remark: "",
                      },
                    ],
                  },
                })
                SuccessToast("Success")
              }}
            />
          )}
      </div>
    )
  }

  const COLUMNS = [
    {
      minWidth: 20,
      Header: "",
      accessor: "loadingSequence",
      Cell({ value }) {
        return (
          <>
            <div className="vt-text-center">{`${value}`}</div>
          </>
        )
      },
    },
    {
      minWidth: 20,
      Header: "",
      accessor: "despatchPhotoLink",
      Cell({ value, index, original, ...props }) {
        return (
          <div>
            <div className="flex items-center">
              <FileUploadDialog
                disabled={false}
                path="photos"
                value={
                  state &&
                  state.length > 0 &&
                  state[index]?.despatchPhotoLink?.length > 0 &&
                  state[index]?.despatchPhotoLink[0]
                    ? state[index]?.despatchPhotoLink
                    : []
                }
                isOpen={
                  state && state.length > 0
                    ? state[index]?.fileUploadOpen
                    : false
                }
                onClose={() => {
                  const tempState = [...state]
                  tempState[index].fileUploadOpen = false
                  setState(tempState)
                }}
                onChange={(path, value) => {
                  const tempState = [...state]
                  tempState[index].despatchPhotoLink = value
                  setState(tempState)
                }}
              />
              <Icon
                className="ml-2"
                style={{ cursor: "pointer" }}
                icon="media"
                iconSize={25}
                intent="success"
                onClick={() => {
                  const tempState = [...state]
                  tempState[index].fileUploadOpen = true
                  setState(tempState)
                }}
              />
            </div>
          </div>
        )
      },
    },
    {
      Header: "Order Id",
      accessor: "orderId",
      Cell({ value }) {
        return (
          <>
            <div className="vt-text-center">{`#${value}`}</div>
          </>
        )
      },
    },
    {
      Header: "Customer's name",
      accessor: "customerName",
      Cell({ value }) {
        return (
          <>
            <div className="vt-text-center">{`${value}`}</div>
          </>
        )
      },
    },
    {
      Header: "Address",
      accessor: "address",
      Cell({ value, index }) {
        return (
          <>
            <div className="vt-text-center">{`${value}`}</div>
            {state &&
              state.length > 0 &&
              state[index].remark &&
              state[index].remark !== "" && (
                <div className="vt-order-dms-missing vt-text-left px-2 py-1 mt-6">
                  {state[index].remark}
                </div>
              )}
          </>
        )
      },
    },
    {
      minWidth: 40,
      Header: "Ordered",
      accessor: "quantity",
      Cell({ value }) {
        return (
          <>
            <div className="vt-text-center">{`${value}`}</div>
          </>
        )
      },
    },
    {
      minWidth: 50,
      Header: "Loaded",
      accessor: "despatchedQuantity",
      Cell: renderEditableCell,
      Footer: (
        <div
          style={{
            backgroundColor: "white",
            background: "white",
            color: "white",
          }}
        >
          <CustomButton
            style={{ float: "right", marginRight: "10px" }}
            icon="confirm"
            intent="success"
            text="Save"
            onClick={() => {
              saveItemDetails({
                variables: {
                  tripId: Number(id),
                  tripIdID: id,
                  bookings: state?.map(booking => ({
                    bookingId: booking.bookingId,
                    despatchedQuantity: booking.despatchedQuantity,
                    remark: "",
                    despatchPhotoLink:
                      booking?.despatchPhotoLink &&
                      booking?.despatchPhotoLink.length > 0 &&
                      booking?.despatchPhotoLink[0]
                        ? {
                            url: booking.despatchPhotoLink[0].url,
                            mediaType: "PHT",
                          }
                        : undefined,
                  })),
                },
              })
              SuccessToast(`Success`)
              setRefresh(new Date().getTime())
            }}
          />
        </div>
      ),
    },
  ]

  return (
    <CustomCard className="m-4">
      <div
        className="p-2 grid grid-cols-12 gap-4"
        style={{ background: "#F5F5F5" }}
      >
        <div>
          <div className="flex">
            <CustomButton
              className="px-2"
              icon="arrow-left"
              onClick={() => navigate(-1)}
              minimal={true}
            />
            <div className="ml-3">
              <div className={Classes.TEXT_LARGE}>
                Gate pass number - {data?.tripDetails?.uuid?.split("-")[0]}
              </div>
              <div
                className={classNames(Classes.TEXT_SMALL, Classes.TEXT_MUTED)}
              >
                Driver name - {data?.tripDetails?.aggregator?.fullName}
              </div>
            </div>
          </div>
        </div>
        <div>
          <CustomTag style={{ float: "right" }} intent="danger">
            <div className="px-2 py-1">
              <div style={{ fontWeight: "500", fontSize: "14px" }}>
                {dayjs(
                  new Date(data?.tripDetails?.estimatedDespatchTime)
                ).format("h:mm a")}
              </div>
              <div className="mx-1" style={{ fontSize: "12px" }}>
                Dispatch
              </div>
            </div>
          </CustomTag>
        </div>
      </div>
      <div className="mt-6 bg-white border-legacy rounded p-3">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-1">
            <div className={classNames(Classes.TEXT_SMALL, Classes.TEXT_MUTED)}>
              Item
            </div>
          </div>
          <div className="col-span-2">
            <div className={classNames(Classes.TEXT_SMALL, Classes.TEXT_MUTED)}>
              Ordered
            </div>
          </div>
          <div className="col-span-2">
            <div className={classNames(Classes.TEXT_SMALL, Classes.TEXT_MUTED)}>
              Loaded
            </div>
          </div>
        </div>

        {state && (
          <div className="grid grid-cols-12 gap-4 vt-order-dms-completed items-center mt-2">
            <div className="col-span-1">
              <div className="flex items-center">
                <div className="">
                  <div className="vt-text-center">
                    {state.length > 0 && state[0]?.itemName}
                  </div>
                  <div
                    className={classNames(
                      "vt-text-left",
                      Classes.TEXT_MUTED,
                      Classes.TEXT_SMALL
                    )}
                  >
                    {state.length > 0 && state[0]?.itemId}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-span-2">
              <div className="">{totalOrdered}</div>
            </div>
            <div className="col-span-2">
              <div className="mt-3">
                <StringInput
                  type="number"
                  value={totalLoaded}
                  onChange={(path, value) => {
                    setTotalLoaded(value)
                  }}
                />
              </div>
            </div>
          </div>
        )}

        <CustomReactTable
          className="vt-dms-react-table mt-6"
          getTrProps={(_, row) => {
            return {
              className: "vt-order-dms-completed",
            }
          }}
          data={state}
          columns={COLUMNS}
        />
      </div>
    </CustomCard>
  )
}

export default ItemDetail
